/**
 * @file useAdmin.js
 *
 * useAdmin
 *
 * Manages user list for the admin panel.
 * Supports search + role filter (client calls refresh({ search, role })).
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { adminService } from "../services/admin";

/**
 * Hook for managing the admin user list with search, role changes, and activation toggles.
 *
 * @returns {{users: Array, isLoading: boolean, refresh: Function, changeRole: Function, toggleActive: Function}}
 */
export function useAdmin() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsers = useCallback(async (params = {}) => {
    try {
      setIsLoading(true);
      const res = await adminService.getUsers(params);
      setUsers(res.data || []);
    } catch {
      // Handled by interceptor
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const changeRole = async (userId, role) => {
    const res = await adminService.updateRole(userId, role);
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...res.data } : u)));
    toast.success("Rol actualizado");
  };

  const toggleActive = async (userId) => {
    const res = await adminService.toggleActive(userId);
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...res.data } : u)));
    toast.success(res.data.isActive ? "Usuario activado" : "Usuario desactivado");
  };

  return { users, isLoading, refresh: fetchUsers, changeRole, toggleActive };
}

/**
 * Fetches platform-wide statistics for the admin dashboard.
 * Pass enabled=true only when the current user is an admin;
 * skipping the call for non-admins prevents a 403 toast on the Dashboard.
 *
 * @param {boolean} [enabled=false] - Whether to actually fetch stats
 * @returns {{stats: Object|null, isLoading: boolean}}
 */
export function useAdminStats(enabled = false) {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    adminService.getStats()
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [enabled]);

  return { stats, isLoading };
}
